import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

// Layout components
import Layout from '../../components/layout/Layout';
import Section from '../../components/layout/Section';
import Container from '../../components/layout/Container';

// Section components
import CallToAction from '../../components/sections/CallToAction';

// UI components
import Card from '../../components/ui/Card';

// Mock data
import { services } from '../../data/mockData';

// Service icon map keyed by iconName
const serviceIcons: Record<string, React.ReactNode> = {
  education: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    </svg>
  ),
  onboarding: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  workflow: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  development: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  optimization: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  design: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  ),
  code: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    </svg>
  ),
  search: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  marketing: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
    </svg>
  ),
};

const fallbackIcon = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

/**
 * Services overview page
 */
const ServicesPage: React.FC = () => {
  return (
    <Layout>
      {/* Hero section */}
      <div className="relative overflow-hidden bg-neu-bg pt-28 pb-14">
        <div
          className="pointer-events-none absolute top-0 right-0 h-full flex items-center opacity-50 overflow-hidden"
          style={{ width: '38%' }}
        >
          <div
            className="relative flex items-center justify-center"
            style={{ width: 380, height: 380, marginLeft: 'auto', marginRight: '-80px' }}
          >
            <div className="absolute rounded-full" style={{ width: 380, height: 380, boxShadow: '10px 10px 22px rgb(163,177,198,0.5), -10px -10px 22px rgba(255,255,255,0.4)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full" style={{ width: 260, height: 260, boxShadow: 'inset 7px 7px 14px rgb(163,177,198,0.6), inset -7px -7px 14px rgba(255,255,255,0.5)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full" style={{ width: 140, height: 140, boxShadow: '7px 7px 14px rgb(163,177,198,0.6), -7px -7px 14px rgba(255,255,255,0.5)', background: '#E0E5EC' }} />
            <div className="absolute rounded-full animate-float" style={{ width: 56, height: 56, background: 'linear-gradient(135deg, #6C63FF 0%, #8B84FF 100%)', boxShadow: '0 4px 16px rgba(108,99,255,0.45)' }} />
          </div>
        </div>
        <Container>
          <motion.div
            className="relative z-10 max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-neu-text mb-6 leading-tight">
              Our AI Services
            </h1>
            <p className="text-lg md:text-xl text-neu-muted mb-8 max-w-2xl mx-auto leading-relaxed">
              Comprehensive AI solutions designed to transform your business, from initial training to advanced custom development.
            </p>
          </motion.div>
        </Container>
      </div>

      {/* Services grid */}
      <Section bgColor="bg-white" padding="lg">
        <Container>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex"
                >
                  <Link to={service.url} className="w-full h-full">
                    <Card hover className="h-full flex flex-col">
                      <div className="p-6 md:p-8 flex-grow">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 text-primary">
                            {serviceIcons[service.iconName] ?? fallbackIcon}
                          </div>
                          <div className="ml-4">
                            <h2 className="text-xl font-semibold">{service.title}</h2>
                            {service.badge && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary mt-1">
                                {service.badge}
                              </span>
                            )}
                          </div>
                        </div>

                        <p className="text-neutral-700 mb-6">{service.description}</p>

                        <div className="mt-auto pt-2">
                          <span className="inline-flex items-center text-primary font-medium">
                            Learn more
                            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* How we approach AI */}
      <Section bgColor="bg-neutral-50" padding="lg">
        <Container>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Our Approach to AI</h2>
              <p className="text-lg text-neutral-700">
                At CodemyAI, we believe that successful AI implementation requires a thoughtful, human-centered approach.
              </p>
            </div>
            
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <div className="bg-white rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-semibold mb-3">1. Understanding Your Business</h3>
                  <p className="text-neutral-700">
                    We begin every engagement by deeply understanding your business objectives, challenges, and existing processes. This foundation ensures that our AI solutions directly address your specific needs rather than being generic implementations.
                  </p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <div className="bg-white rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-semibold mb-3">2. Human-Centered Design</h3>
                  <p className="text-neutral-700">
                    We design AI solutions with human users in mind. Our focus is on creating intuitive experiences that augment human capabilities rather than replacing them, leading to higher adoption rates and greater business impact.
                  </p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="bg-white rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-semibold mb-3">3. Ethical & Responsible Implementation</h3>
                  <p className="text-neutral-700">
                    We prioritize ethical considerations in all our AI implementations, ensuring transparency, fairness, and respect for privacy. Our solutions are designed to be explainable and accountable, building trust with both employees and customers.
                  </p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="bg-white rounded-lg p-6 border border-neutral-200">
                  <h3 className="text-xl font-semibold mb-3">4. Continuous Improvement</h3>
                  <p className="text-neutral-700">
                    AI implementation is a journey, not a destination. We provide ongoing monitoring, optimization, and iteration of our solutions to ensure they continue to deliver value as your business evolves and AI technologies advance.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Call to action */}
      <CallToAction
        title="Ready to Transform Your Business with AI?"
        description="Schedule a complimentary consultation to discuss how our AI services can address your specific business challenges."
        ctaText="Book Your Free Consultation"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default ServicesPage;